import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { 
  FiTrash, 
  FiRotateCw, 
  FiChevronLeft,  
  FiChevronRight  
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useRestoreProductMutation,
} from "../redux/apislice";
import { Player } from "@lottiefiles/react-lottie-player";


// const dummyShippingDetails = [
//   { destination: "USA", cost: "$10", delivery: "3-5 days" },
//   { destination: "UK", cost: "$15", delivery: "5-7 days" },
//   { destination: "Canada", cost: "$12", delivery: "4-6 days" },
// ];

export default function Card() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;
  const [deleteProduct] = useDeleteProductMutation();
  const [restoreProduct, { isLoading: isRestoring }] = useRestoreProductMutation();
  const { data, isLoading, isError, refetch } = useGetProductsQuery({ page, limit });
  const products = data?.data || [];
  const totalProducts = data?.total || 0;
  const totalPages = Math.ceil(totalProducts / limit);
  
  const [showModal, setShowModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletedProducts, setDeletedProducts] = useState({});

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreProductId, setRestoreProductId] = useState(null);

  useEffect(() => {
    const storedDeletions = localStorage.getItem("productDeletions");
    setDeletedProducts(storedDeletions ? JSON.parse(storedDeletions) : {});
  }, []);

  const goToPage = (newPage) => {
    setPage(newPage);
  };

  const nextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedProductId(id);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedProductId) return;
    setIsDeleting(true);
    try {
      const response = await deleteProduct(selectedProductId).unwrap();
      if (response.status) {
        const newDeletions = {
          ...deletedProducts,
          [selectedProductId]: new Date().toISOString(),
        };
        setDeletedProducts(newDeletions);
        localStorage.setItem("productDeletions", JSON.stringify(newDeletions));
        await refetch();
        toast.success(response.message || "Product deleted successfully");
      } else {
        toast.error(response.message || "Failed to delete product");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Error deleting product");
    } finally {
      setIsDeleting(false);
      setShowModal(false);
      setSelectedProductId(null);
    }
  };

  const canRestoreProduct = (productId) => {
    if (!deletedProducts[productId]) return false;
    const deletionDate = new Date(deletedProducts[productId]);
    const now = new Date();
    const daysSinceDeletion = (now - deletionDate) / (1000 * 60 * 60 * 24);
    return daysSinceDeletion <= 7;
  };

  const handleOpenRestoreModal = (productId) => {
    setRestoreProductId(productId);
    setShowRestoreModal(true);
  };

  const handleConfirmRestore = async () => {
    if (!restoreProductId) return;
    try {
      const res = await restoreProduct(restoreProductId).unwrap();
      const newDeletions = { ...deletedProducts };
      delete newDeletions[restoreProductId];
      setDeletedProducts(newDeletions);
      localStorage.setItem("productDeletions", JSON.stringify(newDeletions));
      await refetch();
      toast.success(res.message || "Product restored successfully!");
    } catch (error) {
      toast.error(error?.data?.message || "Error restoring product");
    } finally {
      setRestoreProductId(null);
      setShowRestoreModal(false);
    }
  };

  const handleEditClick = (product) => {
    const slug = product.title?.toLowerCase().replace(/\s+/g, "-") || product.id.toString();
    navigate(`/product-dashboard/edit-product/${product?.id}`);
  };
   if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
        }}>
          <Player
            autoplay
            loop
            src="https://assets1.lottiefiles.com/packages/lf20_raiw2hpe.json"
            style={{ height: '150px', width: '150px' }}
          />
        </div>
      );
    }
  if (isError) return <div className="text-center text-red-500 py-20">Failed to load products.</div>;

  return (
    <>
      {products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
          
          <h2 className="text-2xl font-bold mt-6 mb-2">Your product catalog is empty.</h2>
          <p className="text-gray-600 max-w-md mb-6">
             This is your opportunity to start something great! 
            Add your first product and begin your selling journey today.
          </p>
          <button
            onClick={() => navigate('/product-dashboard/add-product')}
            className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800 transition-colors"
          >
            Add Your First Product
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-4 sm:px-6 md:px-8 py-8 ">
            {products.map((item) => {
              const slug = item.title?.toLowerCase().replace(/\s+/g, "-") || item.id.toString();
              // const shippingDetails = item.shippingInfo?.length ? item.shippingInfo : dummyShippingDetails;

              const isDeleted = item.status === "DELETED";
              const showRestore = isDeleted && canRestoreProduct(item.id);
              const daysLeftToRestore = isDeleted && deletedProducts[item.id]
                ? Math.ceil(7 - (new Date() - new Date(deletedProducts[item.id])) / (1000 * 60 * 60 * 24))
                : 0;

              const status = isDeleted
                ? "Deleted"
                : item.stock > 5
                  ? item.status
                  : "Out of Stock";

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true }}
                  className={`group bg-white shadow rounded-xl flex flex-col gap-4 relative overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${isDeleted ? "opacity-70" : ""}`}
                  onClick={() => !isDeleted && navigate(`/product/${slug}`)}
                >
                  {isDeleted && (
                    <div className="absolute top-0 left-0 w-full bg-red-600 text-white text-center py-1 text-sm font-medium z-20">
                      This product is deleted from list
                      {showRestore && (
                        <div className="text-xs">Can be restored within {daysLeftToRestore} days</div>
                      )}
                    </div>
                  )}

                  <div className="relative w-full">
                    {/* {!isDeleted && (
                      <div className="absolute top-2 left-2 z-20">
                        <span className="bg-black text-white px-2 py-0.5 rounded text-xs font-bold">-40%</span>
                      </div>
                    )} */}

                    {!isDeleted && (
                      <button
                        aria-label="Delete product"
                        className="absolute top-2 right-2 z-20"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(item.id);
                        }}
                      >
                        <FiTrash className="w-5 h-5 text-white hover:text-red-500 transition-colors" />
                      </button>
                    )}

                    <img
                      src={item.images?.url}
                      alt={item.title}
                      className={`w-full h-48 object-cover rounded-t-xl ${isDeleted ? "grayscale" : ""}`}
                    />
                  </div>

                  <div className="px-0 pb-0 leading-normal ml-4 mr-4 mb-4">
                    <h3
                      className={`text-md font-semibold ${isDeleted ? "line-through" : ""} line-clamp-1`}
                    >
                      {item.title}
                    </h3>
                    <div className="flex gap-2 text-sm items-center mt-1">
                      <span className="text-red-500 font-bold">Rs {item.on_price}</span>
                      {!isDeleted && <span className="line-through text-gray-400 text-xs">Rs {item.off_price}</span>}
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${status === "ACTIVE" || status === "Active"
                            ? "bg-green-100 text-green-700"
                            : status === "DELETED" || status === "Deleted"
                              ? "bg-red-100 text-red-700"
                              : status === "PENDING"
                                ? "bg-yellow-100 text-yellow-700"
                                : status === "SOLD_OUT"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 mt-1">
                      <div className="flex gap-1 items-center">🇺🇸 <span>3.5</span></div>
                      <span>3–7 days</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                      <span>👁️ {item.impressions || 0} views</span>
                      <span>🖱️ {item.clicks || 0} clicks</span>
                    </div>
                    <div >
                      {isDeleted ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRestoreModal(item.id);
                          }}
                          className="w-full bg-blue-600 text-white rounded-md py-2 mt-4 hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
                        >
                          <FiRotateCw /> Restore Product ({daysLeftToRestore} days left)
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            handleEditClick(item);
                          }}
                          className="z-20 w-full bg-black text-white rounded-md py-2 mt-4 hover:bg-gray-800 text-sm text-center relative"
                        >
                          Edit Product
                        </button>
                      )}
                    </div>
                  </div>

                  {/* {!isDeleted && (
                    <div className="absolute bottom-0 left-0 w-full max-h-[55%] bg-white rounded-xl p-4 translate-y-full group-hover:translate-y-0 transition-all duration-700 ease-in-out shadow-lg flex flex-col justify-start z-10 pb-16">
                      <h4 className="font-bold mb-2">Shipping Details</h4>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-700">
                            <th>Destination</th>
                            <th>Cost</th>
                            <th>Delivery</th>
                          </tr>
                        </thead>
                        <tbody className="overflow-auto">
                          {shippingDetails?.slice(0,3)?.map((detail, index) => (
                            <tr key={index} className="border-t">
                              <td className="py-1">{detail.destination}</td>
                              <td className="py-1">{detail.cost}</td>
                              <td className="py-1">{detail.delivery}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )} */}
                </motion.div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 pb-8 mt-4">
              <button
                onClick={prevPage}
                disabled={page === 1}
                className={`flex items-center gap-1 px-4 py-2 rounded ${
                  page === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                <FiChevronLeft /> Previous
              </button>

              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        page === pageNum ? 'bg-gray-800 text-white' : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={nextPage}
                disabled={page === totalPages}
                className={`flex items-center gap-1 px-4 py-2 rounded ${
                  page === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                Next <FiChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-center text-lg font-semibold mb-6">Are you sure you want to delete this product?</h2>
            <p className="text-center text-sm text-gray-600 mb-4">You can restore it within 7 days after deletion.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-4 py-2 border rounded hover:bg-gray-100 transition"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className={`w-full sm:w-auto px-4 py-2 rounded text-white ${isDeleting ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                  }`}
              >
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-center text-lg font-semibold mb-6">Restore Product?</h2>
            <p className="text-center text-sm text-gray-600 mb-4">
              You can restore this product within 7 days. Proceed?
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="w-full sm:w-auto px-4 py-2 border rounded hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRestore}
                className="w-full sm:w-auto px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                {isRestoring ? "Restoring..." : "Confirm restore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}